import React from 'react';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { Icon } from '../../../components/ui/Icon';
import { formatFileSize } from '../../../utils/formatters';

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const FieldLabel = styled.label`
  font-weight: 600;
  color: var(--text-primary);
`;

const HelperText = styled.p`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--danger-600);
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-md);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: var(--space-md);
  background: var(--surface-strong);
`;

const FileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const FileName = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const FileMeta = styled.span`
  font-size: var(--font-size-sm);
  color: var(--text-muted);
`;

const HiddenInput = styled.input`
  display: none;
`;

interface ComplianceFileFieldProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  error?: string | null;
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
}

export const ComplianceFileField: React.FC<ComplianceFileFieldProps> = ({
  file,
  onFileChange,
  error,
  disabled = false,
  helperText,
  required = false,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const reactId = React.useId();
  const inputId = `compliance-file-${reactId}`;
  const helperId = helperText ? `${inputId}-help` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy =
    [helperId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <FieldContainer>
      <FieldLabel htmlFor={inputId}>Archivo de respaldo</FieldLabel>
      {helperText && <HelperText id={helperId}>{helperText}</HelperText>}
      <HiddenInput
        ref={inputRef}
        id={inputId}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        onChange={(event) => {
          const selectedFile = event.target.files?.[0] ?? null;
          onFileChange(selectedFile);
        }}
      />
      {file ? (
        <FileCard>
          <Icon name="file" size="sm" aria-hidden />
          <FileInfo>
            <FileName>{file.name}</FileName>
            <FileMeta>
              {formatFileSize(file.size)} · {file.type || 'Archivo'}
            </FileMeta>
          </FileInfo>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => {
              onFileChange(null);
              if (inputRef.current) {
                inputRef.current.value = '';
              }
            }}
            disabled={disabled}
            type="button"
          >
            Quitar
          </Button>
        </FileCard>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          type="button"
          aria-describedby={describedBy}
          aria-invalid={Boolean(error) || undefined}
          aria-required={required || undefined}
        >
          <Icon name="file-upload" size="sm" aria-hidden />
          Seleccionar archivo
        </Button>
      )}
      {error && (
        <ErrorText role="alert" id={errorId}>
          {error}
        </ErrorText>
      )}
    </FieldContainer>
  );
};

ComplianceFileField.displayName = 'ComplianceFileField';
